/**
 * Manage Protocol Dialog - Improved UX for uploading APP and WEB protocols
 * Shows separate buttons for each platform with status indicators
 */

import React, {useState, useCallback, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Paper,
  TextField,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {apiService} from '../services/api';

interface ManageProtocolDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (uploadedPlatform?: string) => void;
  trialId?: string;
  existingPlatforms?: ('app' | 'web')[];
  appFileName?: string | null;
  webFileName?: string | null;
}

interface PlatformStatus {
  exists: boolean;
  lastModified?: string;
  fileName?: string; // Original filename with extension
}

export const ManageProtocolDialog: React.FC<ManageProtocolDialogProps> = ({
  open,
  onClose,
  onSuccess,
  trialId: preselectedTrialId,
  existingPlatforms = [],
  appFileName: propAppFileName,
  webFileName: propWebFileName,
}) => {
  const [trialId, setTrialId] = useState(preselectedTrialId || '');
  const [appStatus, setAppStatus] = useState<PlatformStatus>({exists: false});
  const [webStatus, setWebStatus] = useState<PlatformStatus>({exists: false});
  const [uploadingApp, setUploadingApp] = useState(false);
  const [uploadingWeb, setUploadingWeb] = useState(false);
  const [appProgress, setAppProgress] = useState(0);
  const [webProgress, setWebProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingPlatform, setPendingPlatform] = useState<'app' | 'web' | null>(null);

  // Check existing protocols when dialog opens
  const checkExistingProtocols = useCallback(() => {
    if (!trialId && !preselectedTrialId) return;
    
    const currentTrialId = trialId || preselectedTrialId;
    
    // Load filenames: prioritize props from API, fall back to sessionStorage
    const appFileName = propAppFileName || sessionStorage.getItem(`protocol_filename_${currentTrialId}_app`);
    const webFileName = propWebFileName || sessionStorage.getItem(`protocol_filename_${currentTrialId}_web`);
    
    // Check APP platform
    if (existingPlatforms.includes('app')) {
      setAppStatus({exists: true, fileName: appFileName || undefined});
    } else {
      setAppStatus({exists: false});
    }

    // Check WEB platform
    if (existingPlatforms.includes('web')) {
      setWebStatus({exists: true, fileName: webFileName || undefined});
    } else {
      setWebStatus({exists: false});
    }
  }, [trialId, preselectedTrialId, existingPlatforms, propAppFileName, propWebFileName]);

  useEffect(() => {
    if (open) {
      if (preselectedTrialId) {
        setTrialId(preselectedTrialId);
      }
      checkExistingProtocols();
      setError(null);
      setSuccessMessage(null);
    }
  }, [open, preselectedTrialId, checkExistingProtocols]);

  const handleFileSelect = async (platform: 'app' | 'web') => {
    // Validate trial ID first
    if (!trialId) {
      setError('Please enter a Trial ID first');
      return;
    }

    // Check if protocol already exists - show confirmation dialog
    const platformExists = platform === 'app' ? appStatus.exists : webStatus.exists;
    
    if (platformExists) {
      setPendingPlatform(platform);
      setConfirmDialogOpen(true);
      return;
    }

    // No existing protocol - proceed directly
    openFilePicker(platform);
  };

  const openFilePicker = (platform: 'app' | 'web') => {
    // Create file input for protocol files (.zip or .protocol)
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,.protocol';
    
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      // Validate file extension
      const fileName = file.name.toLowerCase();
      const isValidExtension = fileName.endsWith('.zip') || fileName.endsWith('.protocol');
      
      if (!isValidExtension) {
        setError('Please select a ZIP or .protocol file');
        return;
      }
      
      await handleUpload(file, platform);
    };

    input.click();
  };

  const handleConfirmReplace = () => {
    setConfirmDialogOpen(false);
    if (pendingPlatform) {
      openFilePicker(pendingPlatform);
      setPendingPlatform(null);
    }
  };

  const handleCancelReplace = () => {
    setConfirmDialogOpen(false);
    setPendingPlatform(null);
  };

  const handleUpload = async (
    file: File, 
    platform: 'app' | 'web'
  ) => {
    const setUploading = platform === 'app' ? setUploadingApp : setUploadingWeb;
    const setProgress = platform === 'app' ? setAppProgress : setWebProgress;
    const platformName = platform.toUpperCase();

    // Keep the full filename with extension
    const fileName = file.name;

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);
      setWarningMessage(null);
      setProgress(0);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 300);

      console.log(`Uploading ${platformName} protocol: trial_id=${trialId}`);

      // Upload protocol
      const response = await apiService.uploadProtocol(trialId, file, platform);

      clearInterval(progressInterval);
      setProgress(100);

      // Show success message
      const action = (platform === 'app' && appStatus.exists) || (platform === 'web' && webStatus.exists)
        ? 'replaced'
        : 'uploaded';
      setSuccessMessage(`${platformName} protocol ${action} successfully!`);

      // Check for warning in response (success is true but error field exists)
      if (response.success && response.error) {
        setWarningMessage(response.error);
      }

      // Persist filename to sessionStorage for current session
      sessionStorage.setItem(`protocol_filename_${trialId}_${platform}`, fileName);

      // Update status immediately to show green checkmark with filename
      if (platform === 'app') {
        setAppStatus({exists: true, fileName});
      } else {
        setWebStatus({exists: true, fileName});
      }

      // Refresh status and notify parent
      setTimeout(() => {
        setProgress(0);
        onSuccess(platform);
      }, 1000);
    } catch (err: any) {
      console.error(`Error uploading ${platformName} protocol:`, err);
      
      // Provide user-friendly error messages
      let errorMessage = 'An invalid file was provided, please select a valid ZIP or .protocol file and try again';
      
      // Check for specific error types
      if (err.response?.status === 400) {
        errorMessage = 'An invalid file was provided, please select a valid ZIP or .protocol file and try again';
      } else if (err.response?.status === 500) {
        errorMessage = 'An invalid file was provided, please select a valid ZIP or .protocol file and try again';
      } else if (err.response?.status === 413) {
        errorMessage = 'The file is too large. Please ensure the file is under the size limit';
      } else if (err.message?.toLowerCase().includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again';
      } else if (err.message?.toLowerCase().includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again with a smaller file';
      }
      
      setError(errorMessage);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploadingApp && !uploadingWeb) {
      setError(null);
      setSuccessMessage(null);
      setWarningMessage(null);
      setAppProgress(0);
      setWebProgress(0);
      if (!preselectedTrialId) {
        setTrialId('');
      }
      onClose();
    }
  };

  const isAnyUploading = uploadingApp || uploadingWeb;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" component="div">
            Manage Protocol
          </Typography>
          {preselectedTrialId && (
            <Typography variant="body2" color="text.secondary">
              Trial ID: {preselectedTrialId}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{mt: 2}}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{mb: 3}} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {successMessage && (
            <Alert severity="success" sx={{mb: 3}} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* Warning Alert */}
          {warningMessage && (
            <Alert severity="warning" sx={{mb: 3}} onClose={() => setWarningMessage(null)}>
              {warningMessage}
            </Alert>
          )}

          {/* Trial ID Input - Only show if NOT preselected */}
          {!preselectedTrialId && (
            <TextField
              label="Trial ID"
              fullWidth
              required
              value={trialId}
              onChange={(e) => setTrialId(e.target.value)}
              disabled={isAnyUploading}
              placeholder="e.g., A-1426-0303"
              helperText="Enter trial ID or it will be auto-extracted from filename"
              sx={{mb: 3}}
            />
          )}

          {/* Protocol Upload Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
              gap: 3,
            }}
          >
            {/* APP Protocol Card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  APP Protocol
                </Typography>
              </Box>

              {/* Status Indicator */}
              {appStatus.exists && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(46, 125, 50, 0.2)'
                        : 'success.light',
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: 1,
                  }}
                >
                  <CheckIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'success.main'
                          : 'success.dark',
                    }}
                  />
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'success.main'
                            : 'success.dark',
                      }}
                    >
                      Protocol Uploaded
                    </Typography>
                    {appStatus.fileName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.light'
                              : 'success.dark',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={appStatus.fileName}
                      >
                        {appStatus.fileName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {!appStatus.exists && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No APP protocol uploaded yet
                  </Typography>
                </Box>
              )}

              {/* Upload Progress */}
              {uploadingApp && (
                <Box>
                  <LinearProgress variant="determinate" value={appProgress} />
                  <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                    Uploading... {appProgress}%
                  </Typography>
                </Box>
              )}

              {/* Upload Button */}
              <Button
                variant={appStatus.exists ? 'outlined' : 'contained'}
                startIcon={<UploadIcon />}
                onClick={() => handleFileSelect('app')}
                disabled={isAnyUploading || !trialId}
                fullWidth
              >
                {appStatus.exists ? 'Replace APP' : 'Upload APP'}
              </Button>
            </Paper>

            {/* WEB Protocol Card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  WEB Protocol
                </Typography>
              </Box>

              {/* Status Indicator */}
              {webStatus.exists && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(46, 125, 50, 0.2)'
                        : 'success.light',
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: 1,
                  }}
                >
                  <CheckIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'success.main'
                          : 'success.dark',
                    }}
                  />
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'success.main'
                            : 'success.dark',
                      }}
                    >
                      Protocol Uploaded
                    </Typography>
                    {webStatus.fileName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.light'
                              : 'success.dark',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={webStatus.fileName}
                      >
                        {webStatus.fileName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {!webStatus.exists && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No WEB protocol uploaded yet
                  </Typography>
                </Box>
              )}

              {/* Upload Progress */}
              {uploadingWeb && (
                <Box>
                  <LinearProgress variant="determinate" value={webProgress} />
                  <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                    Uploading... {webProgress}%
                  </Typography>
                </Box>
              )}

              {/* Upload Button */}
              <Button
                variant={webStatus.exists ? 'outlined' : 'contained'}
                startIcon={<UploadIcon />}
                onClick={() => handleFileSelect('web')}
                disabled={isAnyUploading || !trialId}
                fullWidth
              >
                {webStatus.exists ? 'Replace WEB' : 'Upload WEB'}
              </Button>
            </Paper>
          </Box>

          {/* Helper Text */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Tip:</strong> Click the upload button to select a ZIP file or .protocol file for upload.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isAnyUploading}>
          Close
        </Button>
      </DialogActions>

      {/* Confirmation Dialog for Replace */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelReplace}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Protocol Replacement</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{mb: 2}}>
            <Typography variant="body2" fontWeight="medium">
              This will overwrite the existing {pendingPlatform?.toUpperCase()} protocol
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to replace the existing {pendingPlatform?.toUpperCase()} protocol? 
            This action will permanently overwrite the previously uploaded protocol files and all existing sessions will become invalid.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
            <strong>Note:</strong> Make sure you have a backup of your current protocol on your local filesystem 
            before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplace} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmReplace} variant="contained" color="warning">
            Replace Protocol
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
